<template>
  <div>
    <el-form :ref="'form-' + memberId" :model="member" :rules="rules" label-position="top">
      <el-row :gutter="60">
        <el-col :span="24" :md="24" :sm="24" :xs="24">
          <upload-item :prop="member" @updateImageVisibility="updateImageVisibility" @imageUpdated="handleImageUpdated" :type="'members'" :label="'Members'"/>
        </el-col>
      </el-row>
      <h3 class="f-light">Personal Details</h3>
      <el-row :gutter="60">
        <el-col :span="24" :md="8">
          <el-form-item prop="_id" :label="memberIdLabel">
            <el-input disabled v-model="member._id"></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="24" :md="8" v-if="national === 15">
          <el-form-item prop="meta.nationalMemberId" label="Archery Member Number">
            <el-input v-model="value.meta.memberNumber" placeholder="Archery Member Number" disabled />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item>
            <template #label>
              Public Profile Display
              <el-tooltip
                content="If disabled, member details will be removed from Live Score including team lists and teams"
                placement="top"
                effect="light"
              >
              <i class="el-icon-info mr-1"></i>
              </el-tooltip>
            </template>
            <el-select v-model="member.isPublic" placeholder="Select visibility">
              <el-option :value="true" label="Public"></el-option>
              <el-option :value="false" label="Private"></el-option>
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="60">
        <el-col :span="24" :md="8" class="mt-10">
          <el-input-title required>First Name {{ national === 10 ? '(legal)' : ''}} </el-input-title>
          <el-form-item prop="profile.firstName">
            <el-input
              class="mt-05"
              v-model="member.profile.firstName"
              placeholder="Type here..."
              disabled
            />
          </el-form-item>
        </el-col>
        <el-col :span="24" :md="8" class="mt-10">
          <el-input-title required>Last Name {{ national === 10 ? '(legal)' : ''}}</el-input-title>
          <el-form-item prop="profile.lastName">
            <el-input
              class="mt-05"
              v-model="member.profile.lastName"
              placeholder="Type here..."
              disabled
            />
          </el-form-item>
        </el-col>
        <el-col :span="24" :md="8" class="mt-10" v-if="national !== 10">
          <el-input-title>Preferred Name</el-input-title>
          <el-form-item prop="profile.preferredName">
            <el-input
              class="mt-05"
              v-model="member.profile.preferredName"
              placeholder="Type here..."
            />
          </el-form-item>
        </el-col>
        <el-col :span="24" :md="8" class="mt-10" v-if="national !== 10">
          <el-input-title>Short Name (Abbreviation)</el-input-title>
          <el-form-item prop="profile.abbreviation">
            <el-input
              class="mt-05"
              v-model="member.profile.abbreviation"
              placeholder="Type here..."
            />
          </el-form-item>
        </el-col>
        <el-col :span="24" :md="8" class="mt-10">
          <el-input-title required>Gender</el-input-title>
          <el-form-item prop="profile.gender">
            <el-select
              class="mt-05 w-100"
              v-model="member.profile.gender"
              placeholder="Select"
            >
              <el-option
                v-for="item in genders"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              >
              </el-option>
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="24" :md="8" class="mt-10">
          <el-input-title>Preferred Pronouns</el-input-title>
          <el-form-item prop="profile.preferredPronouns">
            <el-input
              class="mt-05"
              v-model="member.profile.preferredPronouns"
              placeholder="e.g., he/him, she/her, they/them"
            />
          </el-form-item>
        </el-col>
         <el-col :span="24" :md="8" class="mt-10" v-if="national === 10">
          <el-input-title required>Preferred First Name</el-input-title>
          <el-form-item prop="profile.preferredFirstName">
            <el-input
              class="mt-05"
              v-model="member.profile.preferredFirstName"
              placeholder="Type here..."
            />
          </el-form-item>
        </el-col>
        <el-col :span="24" :md="8" class="mt-10">
          <el-input-title required>Date of Birth</el-input-title>
          <el-form-item prop="profile.dob">
            <el-input
              class="mt-05"
              :value="member.profile.dob | formatDob"
              placeholder="Type here..."
              disabled
            />
          </el-form-item>
        </el-col>
        <el-col :span="24" :md="8" class="mt-10">
          <el-input-title :required="national !==4">Country</el-input-title>
          <el-form-item prop="profile.nationality">
            <!-- <el-autocomplete
              class="mt-05 w-100"
              v-model="member.profile.nationality"
              :fetch-suggestions="searchCountry"
              placeholder="Select"
            ></el-autocomplete> -->
            <el-select
              filterable
              remote
              reserve-keyword
              class="mt-05 w-100"
              v-model="member.profile.nationality"
              @blur="checkNationality(`form-${memberId}`)"
              :remote-method="searchCountry"
              placeholder="Select">
              <el-option
                v-for="item in countryItems"
                :key="item.value"
                :label="item.label"
                :value="item.value">
              </el-option>
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-divider />
      <el-row type="flex" align="middle" :gutter="10">
        <el-col :span="16">
          <h3>Contact Details</h3>
        </el-col>
        <el-col :span="14" class="d-flex flex-justify-end justify-content-end" v-if="!member.isMainProfile">
          <el-checkbox v-model="hasPrimaryDetails">Use Primary Member Details</el-checkbox>
        </el-col>
      </el-row>
      <el-row :gutter="60">
        <el-col :span="24" :md="12" class="mt-10">
          <el-input-title required>Email Address</el-input-title>
          <el-form-item prop="profile.email">
            <el-input
              class="mt-05"
              v-model="member.profile.email"
              placeholder="Type here..."
            />
          </el-form-item>
        </el-col>
        <el-col :span="24" :md="12" class="mt-10">
          <el-input-title required>Mobile</el-input-title>
          <el-form-item prop="profile.mobile">
            <vue-tel-input
             autoFormat
             autoDefaultCountry
             :defaultCountry="countryCode"
             dropdownOptions.showDialCodeInSelection="false"
             @open="openFlagDropdown = true"
            @close="openFlagDropdown = false"
            @country-changed="handleCountryChanged"
             v-model="member.profile.mobile">
              <template v-slot:arrow-icon>
                <span><i v-if="openFlagDropdown" class="el-icon-arrow-up"></i> <i class="el-icon-arrow-down"></i></span>
              </template>
            </vue-tel-input>
          </el-form-item>
        </el-col>
        <el-col :span="24" class="mt-10 d-flex flex-column-reverse">
          <el-form-item prop="profile.address.formatted">
            <el-input
              v-if="member.profile.address.isManual"
              class="mt-05"
              v-model="member.profile.address.formatted"
              disabled
            />
            <google-auto-complete
              v-else
              class="auto-complete mt-05"
              id="map"
              :key="1"
              ref="address"
              @change="checkAddress($event)"
              @blur="validateAddress"
              @placechanged="getAddressData"
              v-model="member.profile.address.formatted"
              placeholder="Enter an Address"
            >
            </google-auto-complete>
          </el-form-item>
          <div class="d-flex justify-content-between">
            <div>Address<span class="c-red ml-02">*</span></div>
            <el-button
              class="py-0 fs-09 op-7 manualAddressBtn"
              type="text"
              @click="manualAddress"
            >
              {{
                member.profile.address.isManual
                  ? "Search Address"
                  : "Enter Address Manually"
              }}
            </el-button>
          </div>
        </el-col>
        <el-col
          :span="24"
          :md="12"
          class="mt-10"
          v-if="member.profile.address.isManual"
        >
          <el-input-title required>Address Line 1</el-input-title>
          <el-form-item prop="profile.address.addressLine1">
            <el-input
              class="mt-05"
              v-model="member.profile.address.addressLine1"
              placeholder="Type here..."
            />
          </el-form-item>
        </el-col>
        <el-col
          :span="24"
          :md="12"
          class="mt-10"
          v-if="member.profile.address.isManual"
        >
          <el-input-title>Address Line 2</el-input-title>
          <el-form-item prop="profile.address.addressLine2">
            <el-input
              class="mt-05"
              v-model="member.profile.address.addressLine2"
              placeholder="Type here..."
            />
          </el-form-item>
        </el-col>
        <el-col
          :span="24"
          :md="12"
          class="mt-10"
          v-if="member.profile.address.isManual"
        >
          <el-input-title required>City/Suburb</el-input-title>
          <el-form-item prop="profile.address.suburb">
            <el-input
              class="mt-05"
              v-model="member.profile.address.suburb"
              placeholder="Type here..."
            />
          </el-form-item>
        </el-col>
        <el-col
          :span="24"
          :md="12"
          class="mt-10"
          v-if="member.profile.address.isManual"
        >
          <el-input-title :required="nationalId !== 17">State</el-input-title>
          <el-form-item prop="profile.address.state">
            <el-input
              class="mt-05"
              v-model="member.profile.address.state"
              placeholder="Type here..."
            />
          </el-form-item>
        </el-col>
        <el-col
          :span="24"
          :md="12"
          class="mt-10"
          v-if="member.profile.address.isManual"
        >
          <el-input-title required>Postcode</el-input-title>
          <el-form-item prop="profile.address.postcode">
            <el-input
              class="mt-05"
              v-model="member.profile.address.postcode"
              placeholder="Type here..."
            />
          </el-form-item>
        </el-col>
        <el-col
          :span="24"
          :md="12"
          class="mt-10"
          v-if="member.profile.address.isManual"
        >
          <el-input-title required>Country</el-input-title>
          <el-form-item prop="profile.address.country">
            <el-select
              filterable
              remote
              reserve-keyword
              class="mt-05 w-100"
              v-model="member.profile.address.country"
              :remote-method="searchCountry"
              placeholder="Select">
              <el-option
                v-for="item in countryItems"
                :key="item.value"
                :label="item.label"
                :value="item.value">
              </el-option>
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-divider v-if="nationalId !== 19" />
      <h3 v-if="nationalId !== 19">Emergency Contact</h3>
      <el-row :gutter="60" v-if="nationalId !== 19">
        <el-col :span="24" :md="12">
          <el-input-title required>Name</el-input-title>
          <el-form-item prop="profile.emergency.name">
            <el-input
              class="mt-05"
              v-model="member.profile.emergency.name"
              placeholder="Type here..."
            ></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="24" :md="12">
          <el-input-title required>Mobile</el-input-title>
          <el-form-item prop="profile.emergency.mobile">
            <vue-tel-input
             autoFormat
             autoDefaultCountry
             :defaultCountry="countryCode"
             dropdownOptions.showDialCodeInSelection="false"
             @open="openFlagDropdown = true"
            @close="openFlagDropdown = false"
            @country-changed="handleCountryChanged"
             v-model="member.profile.emergency.mobile">
              <template v-slot:arrow-icon>
                <span><i v-if="openFlagDropdown" class="el-icon-arrow-up"></i> <i class="el-icon-arrow-down"></i></span>
              </template>
            </vue-tel-input>
          </el-form-item>
        </el-col>
        <el-col :span="24" :md="12">
          <el-input-title>Relationship</el-input-title>
          <el-form-item prop="profile.emergency.relationship">
            <el-input
              class="mt-05"
              v-model="member.profile.emergency.relationship"
              placeholder="Type here..."
            ></el-input>
          </el-form-item>
        </el-col>
      </el-row>
      <el-divider v-if="nationalId !== 19" />
      <h3 v-if="nationalId !== 19">Parent or Guardian Contact</h3>
      <el-row :gutter="60" v-if="nationalId !== 19">
        <el-col :span="24" :md="8">
          <el-input-title :required="this.isUnder18">Name</el-input-title>
          <el-form-item prop="profile.meta.parentOrGuardian1.name">
            <el-input
              class="mt-05"
              v-model="member.profile.meta.parentOrGuardian1.name"
              placeholder="Type here..."
            ></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="24" :md="8">
          <el-input-title :required="this.isUnder18">Email</el-input-title>
          <el-form-item prop="profile.meta.parentOrGuardian1.email">
            <el-input
              class="mt-05"
              v-model="member.profile.meta.parentOrGuardian1.email"
              placeholder="Type here..."
            ></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="24" :md="8">
          <el-input-title :required="this.isUnder18">Mobile</el-input-title>
          <el-form-item prop="profile.meta.parentOrGuardian1.mobile">
            <vue-tel-input
             autoFormat
             autoDefaultCountry
             :defaultCountry="countryCode"
             dropdownOptions.showDialCodeInSelection="false"
             @open="openFlagDropdown = true"
            @close="openFlagDropdown = false"
            @country-changed="handleCountryChanged"
             v-model="member.profile.meta.parentOrGuardian1.mobile">
              <template v-slot:arrow-icon>
                <span><i v-if="openFlagDropdown" class="el-icon-arrow-up"></i> <i class="el-icon-arrow-down"></i></span>
              </template>
            </vue-tel-input>
          </el-form-item>
        </el-col>
        <!-- <el-col :span="24" :md="8">
          <el-input-title :required="this.isUnder18">Phone</el-input-title>
          <el-form-item prop="profile.meta.parentOrGuardian1.phone">
            <el-input
              class="mt-05"
              v-model="member.profile.meta.parentOrGuardian1.phone"
              placeholder="Type here..."
            ></el-input>
          </el-form-item>
        </el-col> -->
      </el-row>
      <el-divider v-if="![12, 19].includes(national)" />
      <h3 v-if="![12, 19].includes(national)">WWC Information</h3>
      <el-row :gutter="60" v-if="![12, 19].includes(national)">
        <el-col :span="24" :md="8">
          <el-input-title>Number</el-input-title>
          <el-form-item prop="profile.meta.wwc.number">
            <el-input
              class="mt-05"
              v-model="member.profile.meta.wwc.number"
              placeholder="Type here..."
            ></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="24" :md="8">
          <el-input-title>Expiry</el-input-title>
          <el-form-item prop="profile.meta.wwc.expiry">
            <el-date-picker
              class="mt-05"
              v-model="member.profile.meta.wwc.expiry"
              placeholder="Select"
              value-format="timestamp"
              :clearable="false"
            >
            </el-date-picker>
          </el-form-item>
        </el-col>
        <el-col :span="24" :md="8">
          <el-input-title>Issuer</el-input-title>
          <el-form-item prop="profile.meta.wwc.issuer">
            <el-select
              filterable
              class="mt-05"
              v-model="member.profile.meta.wwc.issuer"
              placeholder="Type here..."
            >
              <el-option
                v-for="{ label, value } in states"
                :key="value"
                :label="label"
                :value="value"
              ></el-option>
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <component :is="customMemberDetailForm" :key="key" v-model="member" :refs="$refs[`form-${memberId}`]"/>
      <el-row class="mt-10">
        <el-row :span="24" class="d-flex mt-10" :gutter="20">
          <el-col :span="12" class="d-flex justify-content-end">
            <el-button class="bottomBtn" @click="backToDashboard"
              >Back</el-button
            >
          </el-col>
          <el-col :span="12">
            <el-button
              :disabled="!countryDialCode"
              type="primary"
              class="btn-highlight bottomBtn"
              @click.prevent="save"
              >Save</el-button
            >
          </el-col>
        </el-row>
      </el-row>
    </el-form>
  </div>
</template>

<script>
import _ from 'lodash';
import { calculateMemberAge } from '@/utils/helpers';
import { VueTelInput } from 'vue-tel-input';
import playerDetails from '@/mixins/player-details';
import { clientNationalIds } from '@/utils/client-rules';
import { countries } from '@/utils/constants';
import UploadItem from './UploadItem.vue';

const parstAddressFormatted = (address) => {
  const {
    addressLine1,
    addressLine2,
    suburb,
    state,
    postcode,
    country
  } = address;
  return `${addressLine1}${
    addressLine2 ? ` ${addressLine2}` : ''
  }, ${suburb} ${state}, ${postcode}, ${country}`;
};

export default {
  props: ['value'],
  mixins: [playerDetails],
  components: {
    UploadItem,
    VueTelInput
  },
  data() {
    return {
      nationalId: +process.env.VUE_APP_NATIONAL_ID,
      openFlagDropdown: false,
      countryDialCode: this.getCountryDialCode(),
      countryCode: this.getCountryCode(),
      countryItems: countries,
      countries,
      hasPrimaryDetails: false,
      key: 0,
    };
  },
  mounted() {
    const merged = _.merge({}, this[`${this.currentNational}Data`], this.value);
    console.log('heree');
    console.log(this[`${this.currentNational}Data`]);
    this.$set(this, 'member', merged);
  },
  computed: {

    national() {
      return +process.env.VUE_APP_NATIONAL_ID;
    },
    memberIdLabel() {
      return +process.env.VUE_APP_NATIONAL_ID === 1
        ? 'NSWISA Member ID'
        : 'Member ID';
    },
    customMemberDetailForm() {
      const nationalId = _.includes(clientNationalIds, +process.env.VUE_APP_NATIONAL_ID) ? process.env.VUE_APP_NATIONAL_ID : 0;
      // eslint-disable-next-line
      return () => import('@/views/app/clients/' + nationalId + '-member-profile-extras.vue');
    },
    isUnder18() {
      const endOfYear = this.$store.getters['national/endOfYear'];
      return calculateMemberAge(this.member.profile.dob, endOfYear) < 18;
    },
    userId() {
      return this.$store.getters['user/userId'];
    },
  },
  methods: {
    backToDashboard() {
      const pathName = this.hasCustomEntity ? 'custom-dashboard' : 'dashboard';
      this.$router.push({ name: pathName });
    },
    getCountryDialCode() {
      const nationalId = +process.env.VUE_APP_NATIONAL_ID;
      switch (nationalId) {
        case 17:
          return '64'; // New Zealand
        case 21:
          return '65'; // Singapore
        default:
          return '61'; // Australia (default)
      }
    },

    getCountryCode() {
      const nationalId = +process.env.VUE_APP_NATIONAL_ID;
      switch (nationalId) {
        case 17:
          return 'NZ'; // New Zealand
        case 21:
          return 'SG'; // Singapore
        default:
          return 'AU'; // Australia (default)
      }
    },
    handleCountryChanged(countryCode) {
      this.countryDialCode = countryCode.dialCode;
      this.countryCode = countryCode.iso2.toUpperCase();
    },
    refactorMobile(mobileNumber) {
      let mobile = _.cloneDeep(mobileNumber).replace(/\s/g, '');
      if (mobile.charAt(0) === '0') {
        mobile = mobile.substring(1);
        return `+${ this.countryDialCode }${mobile}`;
      } if (mobile.charAt(0) === '+') {
        return mobile;
      }
      if (!mobile.length) {
        return undefined;
      }
      return `+${ this.countryDialCode }${mobile}`;
    },
    save() {
      this.$refs[`form-${this.memberId}`].validate((valid) => {
        if (valid) {
          // Update formatted for isManual
          if (this.member.profile.address.isManual) {
            this.member.profile.address.formatted = parstAddressFormatted(
              this.member.profile.address
            );
          }
          if (!this.member.profile.address?.addressLine1 || this.member.profile.address?.addressLine1 === 'undefined') {
            if (_.get(this, 'membership.productType') === 'family') this.$emit('validateFamilyDetail', false);
            this.$message.error('Please enter a full address');
            return;
          }
          if (this.member.profile.mobile) {
            this.member.profile.mobile = this.refactorMobile(this.member.profile.mobile);
          }
          if (this.isUnder18) {
            if (!this.member.profile.meta.parentOrGuardian1.name) {
              this.$message.error('Parent or Guardian Name is required');
              return;
            }
            if (!this.member.profile.meta.parentOrGuardian1.email) {
              this.$message.error('Parent or Guardian Email is required');
              return;
            }
            if (!this.member.profile.meta.parentOrGuardian1.mobile) {
              this.$message.error('Parent or Guardian Mobile is required');
              return;
            }
          }

          if (this.member.profile.emergency.mobile) {
            this.member.profile.emergency.mobile = this.refactorMobile(this.member.profile.emergency.mobile);
          }

          if (this.member.profile.meta && this.member.profile.meta.parentOrGuardian1.mobile !== undefined) {
            this.member.profile.meta.parentOrGuardian1.mobile = this.refactorMobile(this.member.profile.meta.parentOrGuardian1.mobile);
          }

          if (this.national === 17) {
            // Auckland requires proof of age
            if (!this.member.meta.proofOfAgeFiles || this.member.meta.proofOfAgeFiles.length === 0) {
              this.$message.error('Proof of age document is required');
              return;
            }
          }

          this.$store.commit('loading', { loading: true, text: 'Loading...' });
          this.$http
            .put(`/assemble/api/v1/account/portal/members/${this.member._id}`, {
              profile: this.member.profile,
              meta: {
                ...this.member.meta,
              },
              isPublic: this.member.isPublic
            })
            .then(() => {
              this.$message.success('Your profile updated successfully');
              this.$store.commit('loading', false);
            })
            .catch((e) => {
              this.$message.error(e.response.data.message);
              this.$store.commit('loading', false);
            });
        }
      });
    },
    searchCountry(query) {
      if (query !== '') {
        setTimeout(() => {
          this.countryItems = this.countries.filter((item) => item.label.toLowerCase()
            .indexOf(query.toLowerCase()) > -1);
        }, 200);
      } else {
        this.countryItems = [];
      }
    },
    manualAddress() {
      this.member.profile.address.isManual = !this.member.profile.address
        .isManual;
    },
    updateImageVisibility(status) {
      this.member.meta.showImg = status;
    },
    handleImageUpdated(data) {
      console.log('Parent received imageUpdated event:', data);
      console.log('Current member imgUrl:', this.member.meta?.imgUrl);

      // Force re-render to show updated image
      this.key++;
      this.$nextTick(() => {
        this.$forceUpdate();
        console.log('Parent force update completed');
        // Additional force update after a short delay
        setTimeout(() => {
          this.$forceUpdate();
          console.log('Parent final force update completed');
        }, 200);
      });
    },
    removeUrlProperties(object) {
      const updatedObject = {};
      _.forEach((Object.entries(object)), ([property, value]) => {
        if (!property.toLowerCase().includes('img') && !property.toLowerCase().includes('url')) {
          updatedObject[property] = value;
        }
      });
      return updatedObject;
    },
    mapping(rData) {
      const {
        email, mobile, address, nationality, emergency, metaProfile
      } = rData.profile;
      const { meta } = rData;

      if (email) this.member.profile.email = email;
      if (mobile) this.member.profile.mobile = mobile;
      if (address) this.member.profile.address = address;
      if (nationality) this.member.profile.nationality = nationality;
      if (emergency) this.member.profile.emergency = emergency;
      if (metaProfile) this.member.profile.meta = this.removeUrlProperties(metaProfile);
      if (meta) this.member.meta = this.removeUrlProperties(meta);
    },
    clearForm(obj) {
      // eslint-disable-next-line no-restricted-syntax, guard-for-in
      for (const key in obj) {
        console.log(key);
        if (!['firstName', 'lastName', 'dob', 'gender'].includes(key)) {
          if (typeof obj[key] === 'object' && obj[key] !== null) {
            this.clearForm(obj[key]);
          } else {
            // eslint-disable-next-line no-param-reassign
            obj[key] = undefined;
          }
        }
      }
    },
  },
  watch: {
    async hasPrimaryDetails(val) {
      if (val) {
        this.$store.commit('loading', { loading: true, text: 'Loading...' });
        const res = await this.$http.get(`/assemble/api/v1/portal/members/primary-details/${this.userId}`);
        console.log(res.data.data.member);
        this.mapping(res.data.data.member);
        this.key++;
        this.$store.commit('loading', false);
      } else {
        this.clearForm(this.member);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.el-date-editor,
.el-select,
.el-input {
  width: 100%;
}

.manualAddressBtn {
  &:hover {
    color: $highlight !important;
  }
  &:focus {
    border-color: #222 !important;
  }
}

</style>
